import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import config from '@plone/volto/registry';
import cx from 'classnames';
import styles from './styles.module.css';
import type { QubicaContentProps, QubicaViewProps } from '../Common/utils';
import BlogBanner from './Components/BlogBanner';
import { Tags } from '../../commons/Tags/Tags';
import type {
  NewsItem,
  Testimonial,
} from '../NewsTestimonialsView/NewsTestimonialView';
import { queryData } from '../../Slots/BlogHomepage/BlogHomepage';
import {
  getAppendedResultsQuery,
  resetGetAppendedResults,
} from '@redturtle/qubicaamf-theme/actions/getAppendedResultsQuery';
import { useAppendedResults } from '@redturtle/qubicaamf-theme/helpers/hooks/useAppendedResults.hook';
import MasonryTemplate from '../../commons/Masonry/MasonryTemplate';
import uniq from 'lodash/uniq';
import flatMap from 'lodash/flatMap';
import { useLocation } from 'react-router-dom';
import { flattenToAppURL } from '@plone/volto/helpers';

export interface NewsMacroViewProps extends QubicaViewProps {
  content: QubicaContentProps & {
    news: NewsItem[];
    testimonials: Testimonial[];
  };
}

const NewsMacroView = ({ content }: NewsMacroViewProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const Container = config.getComponent('Container').component || 'div';

  const news_aggregated_tags =
    content?.news?.length > 0
      ? flatMap(content.news, (item) => item.blog_tags)
      : [];

  const testimonials_aggregated_tags =
    content?.testimonials?.length > 0
      ? flatMap(content.testimonials, (item) => item.blog_tags)
      : [];

  const aggregated_tags = uniq([
    ...news_aggregated_tags,
    ...testimonials_aggregated_tags,
  ]);

  const adaptedQueryData = {
    ...queryData,
    portal_type: ['News Item', 'Testimonial'],
    macro_type_uid: content.UID,
  };

  useEffect(() => {
    dispatch(
      getAppendedResultsQuery(
        '/',
        { ...adaptedQueryData, b_start: 0 },
        content.UID,
        false,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const data = useAppendedResults();

  const subrequestResults = data.subrequests[content.UID];

  const loadMore = () => {
    dispatch(
      getAppendedResultsQuery(
        '/',
        { ...adaptedQueryData, b_start: subrequestResults.items.length },
        content.UID,
        true,
      ),
    );
  };

  useEffect(() => {
    resetGetAppendedResults(content.UID);
  }, [location.pathname, content.UID]);

  return (
    <div
      className={cx(
        'has--fullwidth--true has--bg--gray has--line_color--red has--add_bottom_line--true',
        styles.page,
      )}
    >
      <Container>
        <BlogBanner {...content} />

        {aggregated_tags.length > 0 && (
          <div className={styles.tagContainer}>
            <Tags
              ariaLabel="Blog categories"
              items={aggregated_tags?.map((tag) => ({
                tagName: tag,
                id: tag,
                openLinkInNewTab: false,
                cameFrom: location.pathname,
              }))}
            />
          </div>
        )}

        {!!subrequestResults && subrequestResults.items.length > 0 && (
          <div
            className={cx(styles.masonryContainer, {
              [styles.withMargin]: aggregated_tags.length > 0,
            })}
          >
            <MasonryTemplate
              items={subrequestResults.items}
              loading={subrequestResults.loading}
              totalItems={subrequestResults.total}
              loadMore={loadMore}
            />
          </div>
        )}
      </Container>
    </div>
  );
};

export default NewsMacroView;
